import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { RotateCw, ChevronLeft, ChevronRight, Check } from "lucide-react";

const RATINGS = [
  { key: "again", label: "Again", cls: "bg-rose-500/15 border-rose-400/30 text-rose-300 hover:bg-rose-500/25" },
  { key: "hard", label: "Hard", cls: "bg-amber-500/15 border-amber-400/30 text-amber-300 hover:bg-amber-500/25" },
  { key: "good", label: "Good", cls: "bg-sky-500/15 border-sky-400/30 text-sky-300 hover:bg-sky-500/25" },
  { key: "easy", label: "Easy", cls: "bg-emerald-500/15 border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/25" },
];

export default function FlashcardModal({ open, onOpenChange, cards, branchCards, deckName, onGrade }) {
  const [scope, setScope] = useState("grid");
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(0);

  const hasBranch = Array.isArray(branchCards) && branchCards.length > cards.length;
  const activeCards = scope === "branch" && hasBranch ? branchCards : cards;

  const reset = () => { setI(0); setFlipped(false); setDone(0); };
  const switchScope = (s) => { setScope(s); reset(); };
  const card = activeCards[i];
  const total = activeCards.length;

  const advance = () => {
    setFlipped(false);
    setDone((d) => Math.min(d + 1, total));
    if (i < total - 1) setTimeout(() => setI(i + 1), 120);
  };

  const finished = done >= total && i === total - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="flashcard-modal" className="max-w-lg bg-[#0D0B18] border border-purple-500/25 text-white">
        <DialogTitle className="sr-only">Study flashcards</DialogTitle>
        <DialogDescription className="sr-only">Flip cards and rate your recall.</DialogDescription>

        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-white truncate pr-2">Study · {deckName}</h3>
          <span className="font-mono-fx text-xs text-purple-300 shrink-0">{Math.min(i + 1, total)} / {total}</span>
        </div>

        {hasBranch && (
          <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-[#07070E] border border-purple-500/20">
            <button data-testid="study-scope-grid-btn" onClick={() => switchScope("grid")}
              className={"rounded-lg py-1.5 text-xs font-semibold transition-colors " +
                (scope === "grid" ? "bg-purple-600 text-white" : "text-purple-200/70 hover:text-white")}>
              This Grid ({cards.length})
            </button>
            <button data-testid="study-scope-branch-btn" onClick={() => switchScope("branch")}
              className={"rounded-lg py-1.5 text-xs font-semibold transition-colors " +
                (scope === "branch" ? "bg-purple-600 text-white" : "text-purple-200/70 hover:text-white")}>
              Whole Branch ({branchCards.length})
            </button>
          </div>
        )}

        <div className="h-1.5 w-full rounded-full bg-purple-500/15 overflow-hidden">
          <div className="h-full bg-purple-500 transition-all duration-300" style={{ width: `${(i / total) * 100}%` }} />
        </div>

        {finished ? (
          <div className="py-12 flex flex-col items-center gap-4 text-center" data-testid="flashcard-complete">
            <div className="p-4 rounded-full bg-emerald-500/15 border border-emerald-400/30">
              <Check className="w-8 h-8 text-emerald-300" />
            </div>
            <p className="text-lg font-semibold text-white">Deck complete!</p>
            <p className="text-sm text-purple-200/70">You reviewed all {total} cards.</p>
            <Button data-testid="flashcard-restart-btn" onClick={reset} className="rounded-full bg-purple-600 hover:bg-purple-500">
              <RotateCw className="w-4 h-4 mr-2" /> Restart deck
            </Button>
          </div>
        ) : (
          <>
            <div className="[perspective:1400px] py-2">
              <AnimatePresence mode="wait">
                <motion.div key={`${scope}-${i}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }}
                  onClick={() => setFlipped((f) => !f)} data-testid="flashcard-flip-btn"
                  className="relative w-full h-56 cursor-pointer">
                  <div className={"card-3d absolute inset-0 " + (flipped ? "flipped" : "")}>
                    <div className="card-face absolute inset-0 rounded-2xl p-6 flex flex-col items-center justify-center text-center bg-gradient-to-br from-purple-900/40 to-black/60 border border-purple-500/30 grid-noise">
                      <span className="font-mono-fx text-[10px] uppercase tracking-widest text-purple-400 mb-3">Question</span>
                      <p className="text-lg font-semibold text-white">{card.flashcard_question}</p>
                      <span className="mt-4 text-xs text-purple-300/50">Tap to flip</span>
                    </div>
                    <div className="card-face card-back absolute inset-0 rounded-2xl p-6 flex flex-col items-center justify-center text-center bg-gradient-to-br from-emerald-900/25 to-black/60 border border-emerald-500/30">
                      <span className="font-mono-fx text-[10px] uppercase tracking-widest text-emerald-400 mb-3">Answer</span>
                      <p className="text-base text-emerald-50">{card.flashcard_answer}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {flipped ? (
              <div className="grid grid-cols-4 gap-2">
                {RATINGS.map((r) => (
                  <button key={r.key} data-testid={`flashcard-rate-${r.key}-btn`}
                    onClick={() => { if (onGrade) onGrade(card, r.key); advance(); }}
                    className={"rounded-xl border py-2 text-sm font-medium transition-colors " + r.cls}>
                    {r.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <Button variant="ghost" data-testid="flashcard-prev-btn" disabled={i === 0}
                  onClick={() => { setFlipped(false); setI(Math.max(0, i - 1)); }}
                  className="text-purple-200 hover:text-white hover:bg-purple-500/10">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                </Button>
                <Button variant="ghost" data-testid="flashcard-next-btn"
                  onClick={() => { setFlipped(false); setI(Math.min(total - 1, i + 1)); }}
                  className="text-purple-200 hover:text-white hover:bg-purple-500/10">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
